import React, { useEffect, useCallback } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/NativeSelect";
import InputBase from "@material-ui/core/InputBase";
import { useFormContext } from "react-hook-form";
import ErrorMessage from "./common/ErrorMessage";
import { getError } from "../../utils/validations";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(1),
    minWidth: "100%",
  },
  label: {
    marginBottom: theme.spacing(1),
  },
}));

const Input = withStyles((theme) => ({
  input: {
    borderRadius: 4,
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid",
    borderColor: theme.palette.border.main,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
      borderRadius: 4,
      borderColor: theme.palette.border.main,
      boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
  },
}))(InputBase);

const SelectField = ({ choices, name, label, validations, onSubmit }) => {
  const classes = useStyles();
  const {
    watch,
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
  } = useFormContext();

  const currentValue = watch(name);

  useEffect(() => {
    register(name, getError({ validations }));

    return () => {
      unregister(name, validations);
    };
  }, [name, validations, register, unregister]);

  const submit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleChange = useCallback(
    (event) => {
      setValue(name, event.target.value);

      const isOnlyField = Object.keys(getValues()).length === 1;

      if (isOnlyField) {
        submit();
      }
    },
    [name, setValue, getValues, submit]
  );

  return (
    <FormControl className={classes.formControl}>
      <label htmlFor={name} className={classes.label}>
        {label}
      </label>
      <Select
        input={<Input />}
        name={name}
        value={currentValue}
        onChange={handleChange}
      >
        <option value="" />
        {choices.map(({ key, text }) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </Select>

      <ErrorMessage name={name} />
    </FormControl>
  );
};

SelectField.propTypes = {
  choices: PropTypes.array,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  validations: PropTypes.object,
};

export default SelectField;
